import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, ProviderToken } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { connectState } from '@examdojo/angular/util';
import { AnimationDuration, getAnimationAppearDisappear } from '@examdojo/animation';
import { DebuggingContextService } from '@examdojo/core/debugging';
import { FeatureFlagService } from '@examdojo/core/feature-flag';
import { ErrorHandlerService } from '@examdojo/error-handling';
import { ExamDojoFeatureFlag, ExamdojoFeatureFlags } from '@examdojo/models/feature-flag';
import {
  PracticeActivityCandidateStoreModel,
  PracticeActivityQuery,
  PracticeActivityService,
  PracticeActivityStatus,
  PracticeActivityStrategy,
} from '@examdojo/practice-activity';
import {
  LocalMcqSelectionService,
  McqSelectionService,
  QuestionAttemptEntityQuery,
  QuestionAttemptEntityService,
  QuestionAttemptStoreModel,
} from '@examdojo/question';
import { mapToVoid } from '@examdojo/rxjs';
import { SolutionCaptureQueryParam } from '@examdojo/solution';
import { LoaderComponent } from '@examdojo/ui/loader';
import { assertNonNullable } from '@examdojo/util/assert';
import { isNotNullish } from '@examdojo/util/nullish';
import { IonContent } from '@ionic/angular/standalone';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  delay,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  first,
  map,
  merge,
  Observable,
  of,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import {
  QuestionComponent,
  QuestionGradingRealtimeService,
  removeSolutionCaptureQueryParamWithoutReloadState,
} from '../../../../question-v2';
import { QuestionOldComponent } from '../../../../question-v2/question-old.component';
import { FullscreenFlowService } from '../../../../shared/fullscreen-flow';
import { CloseSolutionCaptureDialogEffectService } from '../../effects/close-solution-capture-dialog-effect.service';
import { FetchAttemptWhenMismatchEffectService } from '../../effects/fetch-attempt-when-mismatch-effect.service';
import { QuestionAttemptFullscreenFlowEffectsService } from '../../effects/question-attempt-fullscreen-effects.service';
import { QuestionAttemptRealtimePracticeService } from '../../effects/question-attempt-realtime-practice.service';
import { PostActivityScreensService } from '../post-activity-screens.service';
import { QuestionSelectionComponent } from '../question-selection/question-selection.component';
import { TopicPracticeV2Service } from '../topic-practice-v2.service';
import { TopicPracticeDialogService } from './topic-practice-dialog.service';

@Component({
  selector: 'dojo-topic-practice-dialog-container',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './topic-practice-dialog-container.component.html',
  imports: [IonContent, QuestionSelectionComponent, QuestionComponent, QuestionOldComponent, LoaderComponent],
  animations: [
    getAnimationAppearDisappear({
      leave: AnimationDuration.None,
    }),
  ],
  host: { class: 'flex flex-col h-full safe-area-top safe-area-bottom' },
  providers: [
    PostActivityScreensService,
    QuestionGradingRealtimeService,
    QuestionAttemptRealtimePracticeService,
    CloseSolutionCaptureDialogEffectService,
    QuestionAttemptFullscreenFlowEffectsService,
    FetchAttemptWhenMismatchEffectService,
    LocalMcqSelectionService,
    { provide: McqSelectionService, useExisting: LocalMcqSelectionService },
  ],
})
export class TopicPracticeDialogContainerComponent {
  constructor(
    private readonly route: ActivatedRoute,
    private readonly location: Location,
    private readonly topicPracticeService: TopicPracticeV2Service,
    private readonly practiceActivityService: PracticeActivityService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly practiceActivityQuery: PracticeActivityQuery,
    private readonly destroyRef: DestroyRef,
    private readonly questionAttemptService: QuestionAttemptEntityService,
    private readonly questionAttemptQuery: QuestionAttemptEntityQuery,
    private readonly debuggingContextService: DebuggingContextService,
    private readonly topicPracticeDialogService: TopicPracticeDialogService,
    private readonly postActivityScreensService: PostActivityScreensService,
    private readonly fullscreenFlowService: FullscreenFlowService,
    private readonly featureFlagService: FeatureFlagService<ExamdojoFeatureFlags>,
  ) {
    merge(
      this.autoSelectSingleCandidateOnPracticeActivityChange(),
      this.fetchPracticeActivityCandidatesOnPracticeActivityChange(),
      this.fetchQuestionAttemptOnPracticeActivityChange(),
      this.setSolutionCaptureQueryParamWithoutReload(),
      this.debuggingContextService.syncDebuggingContext('Activity id', this.inProgressPracticeActivityId$),
    )
      .pipe(takeUntilDestroyed())
      .subscribe();
  }

  private readonly instantiatedServices = [
    QuestionGradingRealtimeService,
    QuestionAttemptRealtimePracticeService,
    CloseSolutionCaptureDialogEffectService,
    QuestionAttemptFullscreenFlowEffectsService,
    FetchAttemptWhenMismatchEffectService,
  ].map((service: ProviderToken<unknown>) => inject(service));

  readonly notStartedPracticeActivityId$: Observable<string | undefined> =
    this.practiceActivityQuery.notStartedPracticeActivityId$;

  readonly inProgressPracticeActivityId$: Observable<string | undefined> =
    this.practiceActivityQuery.inProgressPracticeActivityId$;

  readonly isQuestionCandidatesLoaded$$ = new BehaviorSubject<boolean>(false);
  readonly isQuestionAttemptLoaded$$ = new BehaviorSubject<boolean>(false);

  readonly candidates$ = this.topicPracticeService.candidates$;

  private readonly isPracticeActivityLoaded$ = combineLatest([
    this.questionAttemptQuery.questionLoaded$,
    this.isQuestionCandidatesLoaded$$,
    this.inProgressPracticeActivityId$,
    this.notStartedPracticeActivityId$,
    this.practiceActivityService.creatingPracticeActivity$,
  ]).pipe(
    map(
      ([
        isQuestionAttemptLoaded,
        isQuestionCandidatesLoaded,
        inProgressPracticeActivityId,
        notStartedPracticeActivityId,
        creatingPracticeActivity,
      ]) => {
        if (creatingPracticeActivity) {
          return false;
        }

        if (!inProgressPracticeActivityId && !notStartedPracticeActivityId) {
          // closing the dialog
          return true;
        }

        return (
          (isQuestionAttemptLoaded && !!inProgressPracticeActivityId) ||
          (isQuestionCandidatesLoaded && !!notStartedPracticeActivityId)
        );
      },
    ),
    distinctUntilChanged(),
    debounceTime(200),
  );

  readonly state = connectState({
    ongoingActivityId: this.practiceActivityQuery.ongoingActivityId$,
    inProgressActivityId: this.inProgressPracticeActivityId$,
    notStartedActivityId: this.notStartedPracticeActivityId$,
    candidates: this.candidates$,
    isLoading: this.isPracticeActivityLoaded$.pipe(
      map((loaded) => !loaded),
      startWith(true),
    ),
    areAllQuestionsCompleted: this.topicPracticeService.areAllQuestionsCompleted$,
    isMultiQuestionActivity: this.topicPracticeService.isMultiQuestionActivity$,
    isNewQuestionLayoutEnabled: this.featureFlagService.select(ExamDojoFeatureFlag.NewQuestionLayout),
    isTransitioningBetweenQuestions: this.topicPracticeService.isTransitioningBetweenQuestions$.pipe(
      switchMap((isTransitioning) => {
        if (isTransitioning) {
          return of(true);
        }

        return of(true).pipe(
          // Preventing some layout shifting
          // Maybe we can do it with just CSS
          delay(500),
          map(() => false),
        );
      }),
      startWith(false),
    ),
  });

  handleCandidateSelected(candidate: PracticeActivityCandidateStoreModel) {
    this.isQuestionCandidatesLoaded$$.next(false);

    return this.notStartedPracticeActivityId$
      .pipe(
        filter(isNotNullish),
        take(1),
        switchMap((practiceActivityId) => {
          const firstQuestionId = candidate.questionIds[0];
          assertNonNullable(firstQuestionId, 'firstQuestionId');

          return this.practiceActivityService
            .startPracticeActivity(firstQuestionId, practiceActivityId, candidate.id)
            .pipe(
              // TODO: a custom error title would be nice.
              this.errorHandlerService.catchHttpErrors(() => EMPTY),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  handleCompletedQuestion(action: 'continue' | 'stop') {
    const activityId = this.state.inProgressActivityId || this.state.notStartedActivityId;
    assertNonNullable(activityId, 'activityId');

    if (action === 'stop') {
      this.completeCurrentPracticeActivity(activityId, action).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
      return;
    }

    this.topicPracticeService.areAllQuestionsCompleted$
      .pipe(
        take(1),
        switchMap((allCompleted) => {
          if (allCompleted) {
            return this.completeCurrentPracticeActivity(activityId, action);
          }

          this.topicPracticeService.setTransitioning(true);

          return this.topicPracticeService.nextIncompleteQuestion$.pipe(
            first(isNotNullish),
            withLatestFrom(this.practiceActivityQuery.inProgressPracticeActivityId$),
            switchMap(([questionCompletionEntity, inProgressPracticeActivityId]) =>
              this.questionAttemptService
                .createAttemptForQuestion(
                  questionCompletionEntity.questionId,
                  questionCompletionEntity.lastAttempt?.id ?? null,
                )
                .pipe(map((attempt) => ({ attempt, inProgressPracticeActivityId }))),
            ),
            tap(({ attempt, inProgressPracticeActivityId }) => {
              assertNonNullable(inProgressPracticeActivityId, 'inProgressPracticeActivityId');
              // Update the practice activity store immediately to prevent mismatch detection
              this.practiceActivityService.updatePracticeActivityInStore(inProgressPracticeActivityId, {
                question_attempt_id: attempt.id,
              });
            }),
            switchMap(({ attempt, inProgressPracticeActivityId }) => {
              assertNonNullable(inProgressPracticeActivityId, 'inProgressPracticeActivityId');
              return this.practiceActivityService.fetchQuestionAttempts(attempt.id, inProgressPracticeActivityId);
            }),
            finalize(() => {
              this.topicPracticeService.setTransitioning(false);
            }),
          );
        }),
        this.errorHandlerService.catchHttpErrors(() => this.closeDialogAndReturnHome()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private completeCurrentPracticeActivity(activityId: string, action: 'continue' | 'stop') {
    // TODO: change when new statuses will be introduced
    const createNewPracticeActivity = action === 'continue' ? this.getNewPracticeActivityConfig() : null;

    if (createNewPracticeActivity) {
      this.practiceActivityService.creatingPracticeActivity$$.next(true);
    }

    const practiceActivityStatusToUpdate = this.getPracticeActivityStatusBasedOnQuestionAttemptStatus(
      this.questionAttemptQuery.getActive()?.attempt?.status,
    );

    const presentPostActivityScreens$ =
      action === 'continue'
        ? this.topicPracticeService.isMultiQuestionActivity$.pipe(
            first(),
            switchMap((isMultiQuestionActivity) => {
              if (!isMultiQuestionActivity) {
                return of(null);
              }
              return this.postActivityScreensService.postActivityScreens$.pipe(
                first(),
                switchMap((steps) => this.fullscreenFlowService.open(steps, 1)),
              );
            }),
          )
        : of(null);

    return this.practiceActivityService
      .completePracticeActivityWithStatus(activityId, practiceActivityStatusToUpdate)
      .pipe(
        switchMap(() => presentPostActivityScreens$),
        tap(() => {
          this.practiceActivityService.resetStore();
          this.questionAttemptService.resetStore();
        }),
        switchMap(() => {
          if (!createNewPracticeActivity) {
            return this.closeDialogAndReturnHome();
          }

          return this.practiceActivityService
            .create({
              topics_level_02: createNewPracticeActivity.topicIds,
              strategy: createNewPracticeActivity.strategy,
            })
            .pipe(
              finalize(() => {
                this.practiceActivityService.creatingPracticeActivity$$.next(false);
              }),
            );
        }),
        // If the practice activity creation fails, we should dismiss the dialog and navigate to the home page.
        // Otherwise the user will be stuck in the dialog forever.
        this.errorHandlerService.catchHttpErrors(() => this.closeDialogAndReturnHome()),
        // Do not use takeUntilDestroyed here
        // as after closing the dialog we still want to finish the flow.
      );
  }

  private getNewPracticeActivityConfig() {
    const lastSelection = this.topicPracticeService.topicLevel2SelectionStorageManager.getValue();
    const topicIds = lastSelection?.topicIds ?? [];
    const strategy = lastSelection?.strategy ?? PracticeActivityStrategy.ScoreImpact;

    if (!topicIds?.length) {
      return null;
    }

    return { topicIds, strategy };
  }

  private closeDialogAndReturnHome() {
    return this.topicPracticeDialogService.dismissPracticeActivityDialog();
  }

  private autoSelectSingleCandidateOnPracticeActivityChange() {
    return this.notStartedPracticeActivityId$.pipe(
      filter(isNotNullish),
      switchMap(() => this.candidates$.pipe(first(isNotNullish))),
      this.errorHandlerService.catchHttpErrors(() => EMPTY),
      tap((candidates) => {
        if (candidates.length === 1) {
          this.handleCandidateSelected(candidates[0]);
        } else {
          this.isQuestionCandidatesLoaded$$.next(true);
        }
      }),
    );
  }

  private fetchPracticeActivityCandidatesOnPracticeActivityChange() {
    return this.practiceActivityQuery.ongoingActivity$.pipe(
      map((activity) => activity?.id),
      filter(isNotNullish),
      distinctUntilChanged(),
      switchMap((id) => this.practiceActivityService.fetchPracticeActivityCandidates(id)),
      this.errorHandlerService.catchHttpErrors(() => EMPTY),
    );
  }

  private fetchQuestionAttemptOnPracticeActivityChange() {
    return this.inProgressPracticeActivityId$.pipe(
      filter(isNotNullish),
      distinctUntilChanged(),
      switchMap((activityId) =>
        this.practiceActivityQuery.selectEntity(activityId).pipe(
          map((activity) => activity?.question_attempt_id),
          first(isNotNullish),
          switchMap((attemptId) => this.practiceActivityService.fetchQuestionAttempts(attemptId, activityId)),
        ),
      ),
      mapToVoid(),
    );
  }

  private setSolutionCaptureQueryParamWithoutReload() {
    return this.route.queryParamMap.pipe(
      switchMap((params) =>
        this.questionAttemptQuery.active$.pipe(
          tap(() => {
            if (params.get(SolutionCaptureQueryParam)) {
              this.questionAttemptService.setOpenCaptureDialogFlag(true);
              this.location.replaceState(removeSolutionCaptureQueryParamWithoutReloadState());
            }
          }),
        ),
      ),
    );
  }

  private getPracticeActivityStatusBasedOnQuestionAttemptStatus(
    questionAttemtStatus: QuestionAttemptStoreModel['status'] | undefined,
  ): PracticeActivityStatus {
    return !questionAttemtStatus || questionAttemtStatus === 'PENDING' ? 'DISCARDED' : 'COMPLETED';
  }
}
